/**
 * TamamAlSalah.jsx  —  rebuilt v2
 * -----------------------------------------------------------------
 * Three independent, presentation-only pieces of the "تمام الصلاة"
 * (Complete-your-prayer) feature. The STATE MACHINE lives in the
 * parent (`PrayerTimesWidget`) so that spurious re-renders or a
 * remount cannot spill us back to the welcome screen mid-session.
 *
 * Exports:
 *   • TamamWelcome        — the settings modal shown first
 *   • TamamSession        — the fullscreen rakat counter
 *   • TamamSummaryToast   — brief "You performed N rakats" pill
 *
 * All three portal to `document.body` so parent stacking contexts
 * (backdrop-blur, transforms, etc.) cannot mis-scope them.
 *
 * Design constraints kept intact from v1:
 *   • Night aesthetic (deep starfield background, light-on-dark)
 *   • 76 px rakat circles, positioned in reading order (RTL/LTR)
 *   • 7-second cooldown between successful taps
 *   • 40 px ✕ close button in the top corner opposite the reading start
 *   • Wake Lock keeps the screen on during a session
 *
 * Behavioural changes in v2:
 *   • Brightness reduced from 4 options → 2 (Dim, Bright). Default = Dim.
 *   • Session starts with ONE rakat pre-lit (represents "rakat 1 has
 *     begun"). Each subsequent tap adds one dot (max 4).
 *   • Session component owns NO stage/count state — those live in the
 *     parent. `rakatsDone` is passed in as a controlled prop.
 */

import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { X, Moon, Info } from 'lucide-react';
import { pickLang, tt } from '@/utils/localizedStrings';
import { TAMAM_STRINGS as S, performedRakatsMessage } from '@/utils/tamamStrings';
import {
  getCelestialBackground,
  getTextColors,
  getSurfaceColors,
} from '@/utils/celestialBackground';

/* ============================================================== */
/*                          Constants                              */
/* ============================================================== */

export const TAMAM_MAX_RAKATS = 4;       // Phase 1 — bump to 8 later for Ramadan
export const TAMAM_INITIAL_RAKAT = 1;    // The card opens with rakat #1 already lit
const COOLDOWN_MS = 7_000;
const CIRCLE_SIZE = 76;                  // px
const LS_BRIGHTNESS = 'falak.tamam.brightness';

// Only two brightness levels — default = Dim.
// Brightness now controls ONLY the white circle opacity.
// The background is fixed to the "dim night" appearance regardless of
// the user's choice (`FIXED_OVERLAY`) so that the surrounding ambience
// never changes between presets — only the rakat markers do.
const FIXED_OVERLAY = 0.05;               // constant dark tint on the celestial bg
const BRIGHTNESS_PRESETS = {
  dim:    { circle: 0.375 },              // half of bright
  bright: { circle: 0.75  },              // matches the previous "dim" circle level
};

/* ============================================================== */
/*                    Shared night palette hook                    */
/* ============================================================== */

// Regardless of the actual sun position, Tamam al-Salah always uses the
// Isha (night) aesthetic — deep starfield background with white circles.
const useNightAesthetic = () => useMemo(() => {
  const nightSun = { phase: 'night', altitude: -30 };
  return {
    celestialStyle: getCelestialBackground(nightSun),
    textColors:     getTextColors('night'),
    surfaceColors:  getSurfaceColors('night'),
  };
}, []);

const RTL_LANGS = new Set(['ar', 'ur', 'fa']);
const isRTLLang = (l) => RTL_LANGS.has(l);

/* ============================================================== */
/*                        Welcome modal                            */
/* ============================================================== */

export const TamamWelcome = ({ lang, onStart, onCancel }) => {
  const language = pickLang(lang);
  const isRTL = isRTLLang(language);
  const { celestialStyle, textColors, surfaceColors } = useNightAesthetic();

  // Only two options remain. Any legacy value (auto / med) is coerced to `dim`.
  const [brightness, setBrightness] = useState(() => {
    try {
      const stored = localStorage.getItem(LS_BRIGHTNESS);
      return (stored === 'bright' || stored === 'dim') ? stored : 'dim';
    } catch (_e) { return 'dim'; }
  });

  const handleBrightnessChange = (val) => {
    setBrightness(val);
    try { localStorage.setItem(LS_BRIGHTNESS, val); } catch (_e) { /* noop */ }
  };

  const brightnessOptions = [
    { key: 'dim',    label: tt(S.brightnessDim, language),    dot: BRIGHTNESS_PRESETS.dim.circle },
    { key: 'bright', label: tt(S.brightnessBright, language), dot: BRIGHTNESS_PRESETS.bright.circle },
  ];

  const modal = (
    <div
      className="fixed inset-0 flex items-center justify-center p-4 z-[9998]"
      style={{ background: celestialStyle.background }}
      dir={isRTL ? 'rtl' : 'ltr'}
      data-testid="tamam-welcome-modal"
    >
      {/* Backdrop — click to cancel */}
      <div
        className="absolute inset-0"
        style={{ background: 'rgba(0,0,0,0.35)' }}
        onClick={onCancel}
      />

      <div
        className="relative rounded-3xl p-5 md:p-6 w-full max-w-sm max-h-[92vh] overflow-y-auto"
        style={{
          background: surfaceColors.card,
          border: `1px solid ${surfaceColors.border}`,
          backdropFilter: 'blur(24px)',
          WebkitBackdropFilter: 'blur(24px)',
        }}
      >
        <div className="text-center mb-4">
          <Moon size={30} color={textColors.accent} className="mx-auto mb-2" />
          <div className="text-heading text-xl" style={{ color: textColors.primary }}>
            {tt(S.entryTitle, language)}
          </div>
        </div>

        {/* Mini quadrant demo — 4 semi-transparent dots so the user
            immediately understands the tap-to-count concept. */}
        <div
          className="rounded-xl p-3 mb-4 relative"
          style={{
            background: 'rgba(0,0,0,0.25)',
            height: 110,
            border: `1px dashed ${surfaceColors.border}`,
          }}
        >
          <div className="absolute inset-3 grid grid-cols-2 grid-rows-2 gap-0">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="flex items-center justify-center">
                <div
                  className="rounded-full"
                  style={{
                    width: 22, height: 22,
                    background: textColors.primary,
                    // The first dot is fully lit to preview the new "starts
                    // at rakat 1" behaviour; the other three are faint.
                    opacity: i === 0 ? 0.85 : 0.28,
                  }}
                />
              </div>
            ))}
          </div>
        </div>

        <p className="text-[12px] mb-3 leading-relaxed text-center" style={{ color: textColors.secondary }}>
          {tt(S.welcomeIntro, language)}
        </p>

        <ul className="text-[12px] mb-3 space-y-1.5" style={{ color: textColors.primary }}>
          <li>✦ {tt(S.step1, language)}</li>
          <li>✦ {tt(S.step2, language)}</li>
          <li>✦ {tt(S.step3, language)}</li>
        </ul>

        {/* Brightness — 2 options only in v2 */}
        <div
          className="rounded-lg p-3 mb-3"
          style={{
            background: 'rgba(255,255,255,0.05)',
            border: `1px solid ${surfaceColors.border}`,
          }}
        >
          <div className="text-[11px] mb-2" style={{ color: textColors.secondary }}>
            {tt(S.brightness, language)}
          </div>
          <div className="grid grid-cols-2 gap-2">
            {brightnessOptions.map((opt) => {
              const active = brightness === opt.key;
              return (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => handleBrightnessChange(opt.key)}
                  className="rounded-md py-2 px-2 text-center text-[11px] flex flex-col items-center gap-1.5 transition-all"
                  style={{
                    background: active ? `${textColors.accent}22` : 'transparent',
                    border: active ? `1px solid ${textColors.accent}88` : `1px solid ${surfaceColors.border}`,
                    color: textColors.primary,
                  }}
                  data-testid={`tamam-brightness-${opt.key}`}
                >
                  <div
                    className="rounded-full"
                    style={{
                      width: 14, height: 14,
                      background: textColors.primary,
                      opacity: opt.dot,
                    }}
                  />
                  <span>{opt.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* DND hint */}
        <div
          className="rounded-lg p-2 mb-3 flex items-start gap-2"
          style={{
            background: 'rgba(245, 158, 11, 0.12)',
            border: '1px solid rgba(245, 158, 11, 0.3)',
          }}
        >
          <Info size={13} color="#F59E0B" className="mt-0.5 flex-shrink-0" />
          <div className="text-[11px]" style={{ color: textColors.primary }}>
            {tt(S.dndHint, language)}
          </div>
        </div>

        {/* Start button — hands the chosen brightness back to the parent
            so it becomes the source of truth during the session. */}
        <button
          type="button"
          className="w-full py-3 rounded-xl text-heading text-sm"
          style={{
            background: `linear-gradient(135deg, ${textColors.accent}, ${textColors.accent}dd)`,
            color: '#0d1119',
          }}
          onClick={() => onStart(brightness)}
          data-testid="tamam-start-prayer"
        >
          {tt(S.startPrayer, language)}
        </button>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};

/* ============================================================== */
/*                    Full-screen prayer session                   */
/* ============================================================== */

/**
 * Fully controlled. The parent owns `rakatsDone` and passes both the
 * value and its setter. Local state is limited to short-lived UI
 * concerns (last-tap timestamp for cooldown) so that even if this
 * component gets remounted, the rakat count is preserved.
 */
export const TamamSession = ({
  lang,
  brightness,
  rakatsDone,
  onIncrement,   // called with no args when a valid tap is registered
  onExit,        // called with the current rakat count when user taps ✕
}) => {
  const language = pickLang(lang);
  const isRTL = isRTLLang(language);
  const { celestialStyle } = useNightAesthetic();

  const [lastTapAt, setLastTapAt] = useState(0);
  const wakeLockRef = useRef(null);

  const preset = BRIGHTNESS_PRESETS[brightness] || BRIGHTNESS_PRESETS.dim;

  /* ---- Wake Lock lifecycle ---- */
  useEffect(() => {
    let cancelled = false;
    const acquire = async () => {
      if (!('wakeLock' in navigator)) return;
      try {
        const wl = await navigator.wakeLock.request('screen');
        if (cancelled) { wl.release(); return; }
        wakeLockRef.current = wl;
      } catch (_) { /* user may have denied or unsupported */ }
    };
    acquire();

    const onVisibility = () => {
      if (document.visibilityState === 'visible' && !wakeLockRef.current) acquire();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibility);
      const wl = wakeLockRef.current;
      wakeLockRef.current = null;
      if (wl) { try { wl.release(); } catch (_e) { /* noop */ } }
    };
  }, []);

  /* ---- Lock body scroll while session is active ---- */
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  /* ---- Tap handler ----
   * A tap only counts if:
   *   (a) we haven't hit the 4-rakat ceiling
   *   (b) the 7-second cooldown has elapsed
   */
  const attemptTap = useCallback(() => {
    if (rakatsDone >= TAMAM_MAX_RAKATS) return;
    const now = Date.now();
    if (now - lastTapAt < COOLDOWN_MS) return;
    setLastTapAt(now);
    onIncrement();
  }, [rakatsDone, lastTapAt, onIncrement]);

  /* ---- Circle positions in reading order ---- */
  const orderedPositions = useMemo(() => {
    const positions = {
      TR: { top: '25%',    right: '25%',  transform: 'translate(50%,  -50%)' },
      TL: { top: '25%',    left:  '25%',  transform: 'translate(-50%, -50%)' },
      BR: { bottom: '25%', right: '25%',  transform: 'translate(50%,  50%)' },
      BL: { bottom: '25%', left:  '25%',  transform: 'translate(-50%, 50%)' },
    };
    return isRTL
      ? [positions.TR, positions.TL, positions.BR, positions.BL]
      : [positions.TL, positions.TR, positions.BL, positions.BR];
  }, [isRTL]);

  // Contrast colour for dots and the close-button border.
  const circleColor = '#FFFFFF';

  const handleClose = (e) => {
    e.stopPropagation();
    onExit(rakatsDone);
  };

  const session = (
    <div
      className="fixed inset-0 z-[9999] overflow-hidden select-none"
      style={{
        background: celestialStyle.background,
        touchAction: 'manipulation',
      }}
      onClick={attemptTap}
      data-testid="tamam-session"
      dir={isRTL ? 'rtl' : 'ltr'}
      role="button"
      aria-label={tt(S.entryTitle, language)}
      tabIndex={0}
    >
      {/* Fixed dim overlay — always the same regardless of brightness preset.
          Brightness now only affects the circle opacity below. */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: `rgba(0,0,0,${FIXED_OVERLAY})` }}
      />

      {/* Close button — top corner opposite the reading start */}
      <button
        type="button"
        onClick={handleClose}
        className="absolute rounded-full flex items-center justify-center transition-opacity"
        style={{
          top: 'calc(20px + env(safe-area-inset-top, 0px))',
          ...(isRTL ? { left: 20 } : { right: 20 }),
          width: 40,
          height: 40,
          background: 'rgba(0,0,0,0.40)',
          border: `1.5px solid ${circleColor}`,
          color: circleColor,
          opacity: 0.55,
          zIndex: 10,
          WebkitTapHighlightColor: 'transparent',
        }}
        data-testid="tamam-close-btn"
        aria-label={tt(S.closeSession, language)}
      >
        <X size={20} strokeWidth={2.5} />
      </button>

      {/* Rakat circles */}
      {Array.from({ length: rakatsDone }).map((_, i) => {
        const pos = orderedPositions[i];
        return (
          <div
            key={i}
            className="absolute rounded-full pointer-events-none tamam-rakat-fade-in"
            style={{
              width: CIRCLE_SIZE,
              height: CIRCLE_SIZE,
              background: circleColor,
              '--tamam-final-opacity': preset.circle,
              opacity: preset.circle,
              ...pos,
            }}
            data-testid={`tamam-rakat-dot-${i + 1}`}
          />
        );
      })}
    </div>
  );

  return createPortal(session, document.body);
};

/* ============================================================== */
/*                        Summary toast                            */
/* ============================================================== */

export const TamamSummaryToast = ({ count, lang }) => {
  const language = pickLang(lang);
  const isRTL = isRTLLang(language);
  const { textColors } = useNightAesthetic();

  if (count == null || count <= 0) return null;

  const toast = (
    <div
      className="fixed left-1/2 -translate-x-1/2 z-[9999] tamam-fade-in pointer-events-none"
      style={{ bottom: 'calc(28px + env(safe-area-inset-bottom, 0px))' }}
      data-testid="tamam-summary-toast"
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      <div
        className="rounded-full px-5 py-3 flex items-center gap-2.5 shadow-2xl"
        style={{
          background: 'rgba(15, 23, 42, 0.92)',
          border: `1px solid ${textColors.accent}66`,
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
        }}
      >
        <Moon size={18} color={textColors.accent} />
        <span
          className="text-heading text-sm md:text-base"
          style={{ color: '#F1F5F9' }}
        >
          {performedRakatsMessage(count, language, tt)}
        </span>
      </div>
    </div>
  );

  return createPortal(toast, document.body);
};

/* ============================================================== */
/*        Legacy default export — kept only to avoid breaking      */
/*        any straggling imports; the new API uses named exports.  */
/* ============================================================== */

const TamamAlSalahLegacy = () => null;
export default TamamAlSalahLegacy;
